import { Button, radius } from '@alison/ui';
import ChecklistOutlined from '@mui/icons-material/ChecklistOutlined';
import CloseIcon from '@mui/icons-material/Close';
import EventRepeatOutlined from '@mui/icons-material/EventRepeatOutlined';
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import PlayArrowOutlined from '@mui/icons-material/PlayArrowOutlined';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Drawer from '@mui/material/Drawer';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import { useSlackChannels } from '../../../lib/notifications.hooks.js';
import { EMAIL, RecipientsField, recipientsOf } from '../RecipientsField.js';
import { SegmentedControl, type SegmentedOption } from '../SegmentedControl.js';
import {
  type DraftStep,
  type Frequency,
  firstRunDate,
  frequencyLabel,
  type Pick,
  parsePositions,
  recapLine,
  ruleLine,
  type Schedule,
} from './automationText.js';

const DRAWER_WIDTH = 640;
const MAX_RECIPIENTS = 50;

const FREQUENCIES: Frequency[] = ['once', 'daily', 'weekly', 'monthly'];
const FREQUENCY_OPTIONS: SegmentedOption<Frequency>[] = FREQUENCIES.map((id) => ({
  id,
  label: frequencyLabel(id),
}));

const ENDS_OPTIONS: SegmentedOption<Ends>[] = [
  { id: 'never', label: 'Never' },
  { id: 'on_date', label: 'On date' },
  { id: 'after_runs', label: 'After N runs' },
];

const LABEL_SX = {
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase',
  color: 'text.secondary',
} as const;

const RULE_BADGE_SX = {
  px: 1,
  borderRadius: 0.5,
  fontSize: 10,
  fontWeight: 700,
  bgcolor: 'primary.light',
  color: 'primary.main',
} as const;

type Ends = NonNullable<Schedule['ends']>;
export type Save = 'close' | 'view';

// The form's own shape: every field set, unlike the schema input where defaults may be omitted.
export interface AutomationForm {
  name: string;
  frequency: Frequency;
  timeOfDay: string;
  ends: Ends;
  endDate: string | null;
  maxRuns: number | null;
  emailRecipients: string[];
  slackChannelId: string | null;
  onlyWhenFound: boolean;
}

export interface AutomationSubmit {
  schedule: AutomationForm;
  picks: (Pick | null)[];
}

interface AutomationDrawerProps {
  open: boolean;
  mode: 'create' | 'edit';
  steps: DraftStep[];
  scopeLine: string;
  initial: AutomationForm;
  saving: boolean;
  notice: ReactNode;
  onSave: (submit: AutomationSubmit, then: Save) => void;
  onClose: () => void;
}

interface SectionProps {
  title: string;
  note?: string | null;
  children: ReactNode;
}

interface PickEditorProps {
  step: DraftStep;
  pick: Pick;
  onDone: (pick: Pick) => void;
  onCancel: () => void;
}

export function defaultForm(name: string, email: string | null): AutomationForm {
  return {
    name,
    frequency: 'weekly',
    timeOfDay: '08:00',
    ends: 'never',
    endDate: null,
    maxRuns: null,
    emailRecipients: email === null ? [] : [email],
    slackChannelId: null,
    onlyWhenFound: true,
  };
}

function Section({ title, note = null, children }: SectionProps): ReactNode {
  const [open, setOpen] = useState(true);

  return (
    <Stack sx={{ gap: 2, py: 3, borderTop: '1px solid', borderColor: 'divider' }}>
      <Stack direction="row" sx={{ gap: 1, alignItems: 'baseline' }}>
        <ButtonBase
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          sx={{ gap: 1, borderRadius: 1, alignSelf: 'center' }}
        >
          <ExpandMoreOutlined sx={{ fontSize: 18, transform: open ? 'none' : 'rotate(-90deg)' }} />
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{title}</Typography>
        </ButtonBase>
        {note === null ? null : (
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{note}</Typography>
        )}
      </Stack>
      <Collapse in={open}>
        <Stack sx={{ gap: 2.5, pl: 3.5 }}>{children}</Stack>
      </Collapse>
    </Stack>
  );
}

function StepLine({
  icon,
  title,
  line,
  rule = false,
  onChange,
}: {
  icon: ReactNode;
  title: string;
  line: string;
  rule?: boolean;
  onChange?: () => void;
}): ReactNode {
  return (
    <Stack direction="row" sx={{ gap: 1.5 }}>
      <Box sx={{ color: 'text.secondary', pt: 0.25 }}>{icon}</Box>
      <Stack sx={{ minWidth: 0 }}>
        <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{title}</Typography>
          {rule ? <Box sx={RULE_BADGE_SX}>RULE</Box> : null}
        </Stack>
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
          {line}
          {onChange === undefined ? null : (
            <>
              {' '}
              <Link component="button" type="button" onClick={onChange} sx={{ fontSize: 12 }}>
                Change
              </Link>
            </>
          )}
        </Typography>
      </Stack>
    </Stack>
  );
}

function PickEditor({ step, pick, onDone, onCancel }: PickEditorProps): ReactNode {
  const grouped = step.copy.group_label !== '';
  const [rows, setRows] = useState(pick.indexes.join(', '));
  const [groups, setGroups] = useState(pick.group_indexes?.join(', ') ?? '');
  const indexes = parsePositions(rows);
  // Empty takes every row of each group.
  const groupIndexes = groups.trim() === '' ? null : parsePositions(groups);
  const valid = indexes !== null && (groups.trim() === '' || groupIndexes !== null);

  return (
    <Stack sx={{ gap: 1.5, pl: 4 }}>
      <TextField
        size="small"
        label="Positions"
        value={rows}
        onChange={(event) => setRows(event.target.value)}
        error={indexes === null}
        helperText="Positions in the list as it is ordered, e.g. 1, 2, 5"
        autoFocus
      />
      {grouped ? (
        <TextField
          size="small"
          label={`Positions ${step.copy.group_label}`}
          value={groups}
          onChange={(event) => setGroups(event.target.value)}
          error={groups.trim() !== '' && groupIndexes === null}
          helperText="Leave empty to take them all"
        />
      ) : null}
      <Stack direction="row" sx={{ gap: 1 }}>
        <Button
          variant="outlined"
          size="small"
          disabled={!valid}
          onClick={() =>
            indexes !== null &&
            onDone(grouped ? { indexes, group_indexes: groupIndexes } : { indexes })
          }
        >
          Apply
        </Button>
        <Button variant="text" size="small" onClick={onCancel}>
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
}

export function AutomationDrawer({
  open,
  mode,
  steps,
  scopeLine,
  initial,
  saving,
  notice,
  onSave,
  onClose,
}: AutomationDrawerProps): ReactNode {
  const [form, setForm] = useState(initial);
  const [chips, setChips] = useState(initial.emailRecipients);
  const [draft, setDraft] = useState('');
  const [slackOn, setSlackOn] = useState(initial.slackChannelId !== null);
  const [picks, setPicks] = useState(steps.map((step) => step.pick));
  const [editing, setEditing] = useState<number | null>(null);
  const slack = useSlackChannels(open && slackOn);
  const recipients = recipientsOf(chips, draft);
  const earliestEndDate = firstRunDate(form.frequency, form.timeOfDay);
  const recap = recapLine(steps.map((step, index) => ({ ...step, pick: picks[index] ?? null })));
  const set = (patch: Partial<AutomationForm>): void => setForm({ ...form, ...patch });
  const valid =
    form.name.trim() !== '' &&
    form.timeOfDay !== '' &&
    recipients.length <= MAX_RECIPIENTS &&
    recipients.every((email) => EMAIL.test(email)) &&
    (form.ends !== 'on_date' || (form.endDate !== null && form.endDate >= earliestEndDate)) &&
    (form.ends !== 'after_runs' || (form.maxRuns !== null && form.maxRuns >= 1)) &&
    (!slackOn || form.slackChannelId !== null) &&
    editing === null;

  const handleSave = (then: Save): void =>
    onSave(
      {
        schedule: {
          ...form,
          name: form.name.trim(),
          endDate: form.ends === 'on_date' ? form.endDate : null,
          maxRuns: form.ends === 'after_runs' ? form.maxRuns : null,
          emailRecipients: recipients,
          slackChannelId: slackOn ? form.slackChannelId : null,
        },
        picks,
      },
      then,
    );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        backdrop: { sx: { bgcolor: 'rgba(15, 23, 43, 0.42)', backdropFilter: 'blur(4px)' } },
        paper: {
          'aria-labelledby': 'automation-drawer-title',
          sx: {
            width: DRAWER_WIDTH,
            maxWidth: '96vw',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
          },
        },
      }}
    >
      <Stack direction="row" sx={{ gap: 2, alignItems: 'flex-start', px: 5, py: 4 }}>
        <Box
          sx={{
            display: 'grid',
            placeItems: 'center',
            width: 36,
            height: 36,
            borderRadius: `${radius.lg}px`,
            bgcolor: 'primary.light',
            color: 'primary.main',
          }}
        >
          <EventRepeatOutlined sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography id="automation-drawer-title" component="h2" sx={{ fontSize: 18 }}>
            {mode === 'create' ? 'Create automation' : 'Edit automation'}
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            Scope (inherited from run) · {scopeLine}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Close drawer">
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Stack>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 5 }}>
        <Section title="Session run" note={recap}>
          {steps.map((step, index) => {
            const pick = picks[index] ?? null;
            const title = step.copy.step_title === '' ? step.name : step.copy.step_title;
            return (
              // biome-ignore lint/suspicious/noArrayIndexKey: a step is its position in the chain
              <Stack key={index} sx={{ gap: 2 }}>
                {pick === null ? null : (
                  <>
                    <StepLine
                      icon={<ChecklistOutlined sx={{ fontSize: 16 }} />}
                      title={step.copy.selection_title === '' ? title : step.copy.selection_title}
                      line={ruleLine(pick, step.copy, steps[index - 1]?.copy.order_label ?? '')}
                      rule
                      {...(editing === null ? { onChange: () => setEditing(index) } : {})}
                    />
                    {editing === index ? (
                      <PickEditor
                        step={step}
                        pick={pick}
                        onDone={(next) => {
                          setPicks(picks.map((entry, at) => (at === index ? next : entry)));
                          setEditing(null);
                        }}
                        onCancel={() => setEditing(null)}
                      />
                    ) : null}
                  </>
                )}
                <StepLine
                  icon={<PlayArrowOutlined sx={{ fontSize: 16 }} />}
                  title={title}
                  line={step.copy.step_description}
                />
              </Stack>
            );
          })}
        </Section>

        <Section title="Automation details">
          <Stack sx={{ gap: 1 }}>
            <Typography sx={LABEL_SX}>Name</Typography>
            <TextField
              size="small"
              value={form.name}
              onChange={(event) => set({ name: event.target.value.slice(0, 120) })}
              error={form.name.trim() === ''}
              slotProps={{ htmlInput: { 'aria-label': 'Name' } }}
            />
          </Stack>
          <Stack sx={{ gap: 1 }}>
            <Typography sx={LABEL_SX}>Frequency</Typography>
            <SegmentedControl
              label="Frequency"
              options={FREQUENCY_OPTIONS}
              value={form.frequency}
              onChange={(frequency) => set({ frequency })}
            />
          </Stack>
          <Stack sx={{ gap: 1 }}>
            <Typography sx={LABEL_SX}>Time of day (UTC)</Typography>
            <TextField
              size="small"
              type="time"
              value={form.timeOfDay}
              onChange={(event) => set({ timeOfDay: event.target.value })}
              sx={{ width: 160 }}
              slotProps={{ htmlInput: { 'aria-label': 'Time of day', step: 300 } }}
            />
          </Stack>
          <Stack sx={{ gap: 1 }}>
            <Typography sx={LABEL_SX}>Ends</Typography>
            <SegmentedControl
              label="Ends"
              options={ENDS_OPTIONS}
              value={form.ends}
              onChange={(ends) => set({ ends })}
            />
            {form.ends === 'on_date' ? (
              <TextField
                size="small"
                type="date"
                value={form.endDate ?? ''}
                onChange={(event) => set({ endDate: event.target.value || null })}
                sx={{ width: 200 }}
                slotProps={{ htmlInput: { 'aria-label': 'End date', min: earliestEndDate } }}
              />
            ) : null}
            {form.ends === 'after_runs' ? (
              <TextField
                size="small"
                type="number"
                value={form.maxRuns ?? ''}
                onChange={(event) => {
                  const runs = Number.parseInt(event.target.value, 10);
                  set({ maxRuns: Number.isNaN(runs) ? null : runs });
                }}
                sx={{ width: 160 }}
                slotProps={{ htmlInput: { 'aria-label': 'Number of runs', min: 1 } }}
              />
            ) : null}
          </Stack>
        </Section>

        <Section
          title="Send results automatically"
          note="Deliver the outcome when each run finishes."
        >
          <RecipientsField
            chips={chips}
            draft={draft}
            onChange={(nextChips, nextDraft) => {
              setChips(nextChips);
              setDraft(nextDraft);
            }}
            max={MAX_RECIPIENTS}
            label="Email"
          />
          <Stack direction="row" sx={{ gap: 2, alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={slackOn}
                  onChange={(event) => setSlackOn(event.target.checked)}
                />
              }
              label="Slack"
            />
            <TextField
              select
              size="small"
              label="Slack channel"
              disabled={!slackOn || slack.isError}
              value={form.slackChannelId ?? ''}
              onChange={(event) => set({ slackChannelId: event.target.value || null })}
              helperText={slack.isError ? 'Slack channels are unavailable' : ' '}
              sx={{ flex: 1 }}
            >
              {(slack.data ?? []).map((channel) => (
                <MenuItem key={channel.id} value={channel.id}>
                  #{channel.name}
                </MenuItem>
              ))}
              {/* Keeps a saved channel readable before the list loads. */}
              {form.slackChannelId !== null &&
              !(slack.data ?? []).some((channel) => channel.id === form.slackChannelId) ? (
                <MenuItem value={form.slackChannelId}>{form.slackChannelId}</MenuItem>
              ) : null}
            </TextField>
          </Stack>
          <FormControlLabel
            control={
              <Switch
                checked={form.onlyWhenFound}
                onChange={(event) => set({ onlyWhenFound: event.target.checked })}
              />
            }
            label={
              <Stack>
                <Typography sx={{ fontSize: 13 }}>Only send when results are found</Typography>
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                  A clean scan won't ping anyone
                </Typography>
              </Stack>
            }
          />
        </Section>
      </Box>

      {notice === null ? null : <Box sx={{ px: 5, pb: 2 }}>{notice}</Box>}

      <Stack
        direction="row"
        sx={{
          gap: 1.5,
          justifyContent: 'flex-end',
          px: 5,
          py: 3,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
        {mode === 'create' ? (
          <Button variant="outlined" disabled={!valid || saving} onClick={() => handleSave('view')}>
            Save and view
          </Button>
        ) : null}
        <Button
          variant="gradient"
          disabled={!valid || saving}
          onClick={() => handleSave('close')}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
        >
          {mode === 'create' ? 'Save and close' : 'Save changes'}
        </Button>
      </Stack>
    </Drawer>
  );
}
